// src/oura/components/docs/write/model.ts
//
// What the doc drawer's write views share (Docs phase 2, lane 3: 2.5 to
// 2.8): which views exist, whether a doc can be written at all, the reads
// they make, and the facts they show. Every view reads DOCS_WRITE_ON through
// `canWrite`, so a build missing its switch offers none of them.
//
//   questions  the Questions editor and Send to the doc (B15)
//   lines      Oura's own lines and Rename to the format (B16)
//   share      the pre-share checklist and Share with the client (B17)
//   review     the review email, sent by a named person from Oura (B18)
//   chaser     the chaser, offered only once the reply-by has passed (B18)
//   client-email  the client email for a finished doc: Done and shared (ourteam-backend#6697)
//   thread:id  one comment thread: reply, resolve, reopen, log an answer (lane 4, threads/ThreadSheet)

import {
    DOCS_WRITE_ON,
    getShareCheck,
    listDocMessages,
    lineTwoOf,
    type DocMessageKind,
    type SentDocMessage,
    type SentDocMessageKind,
    type ShareCheck,
} from "@service/aeo/clientDocsWrite";
import type { BusinessDocuments, DocThread, DocumentDetail, StaffDocument } from "@service/aeo/clientDocs";
import { useDocsLoad, type DocsLoad } from "@oura/docs";
import { decidedName, myToday } from "../boardModel";

/** One comment thread of the doc (lane 4): "thread:{threadId}". */
export type ThreadView = `thread:${string}`;

export type DocWriteView = "questions" | "lines" | "share" | "review" | "chaser" | "client-email" | ThreadView;

const VIEWS: readonly string[] = ["questions", "lines", "share", "review", "chaser", "client-email"];

export function isDocWriteView(mode: string): mode is DocWriteView {
    return VIEWS.includes(mode) || isThreadView(mode);
}

export function isThreadView(mode: string): mode is ThreadView {
    return mode.startsWith("thread:") && mode.length > "thread:".length;
}

/** The sub-view's title, under "‹ {doc title}". A thread view titles itself. */
export const WRITE_VIEW_TITLE: Record<Exclude<DocWriteView, ThreadView>, string> = {
    questions: "Questions",
    lines: "Name and title lines",
    share: "Share with the client",
    review: "Review email",
    chaser: "Chaser",
    "client-email": "Email the client",
};

export const MESSAGE_KIND: Record<"review" | "chaser", DocMessageKind> = { review: "doc_review", chaser: "doc_chaser" };

/** Oura writes only Google Docs that are client docs, and only behind the write switch. */
export function canWrite(doc: Pick<StaffDocument, "fileType" | "notClientDoc">): boolean {
    return DOCS_WRITE_ON && doc.fileType === "gdoc" && !doc.notClientDoc;
}

/** Files staff share with the client and mark Done from the drawer: a Google Doc, and a client PDF (3 Nov 2026). */
export function canShare(doc: Pick<StaffDocument, "fileType" | "notClientDoc">): boolean {
    return DOCS_WRITE_ON && (doc.fileType === "gdoc" || doc.fileType === "pdf") && !doc.notClientDoc;
}

/* ------------------------------------------------------------------------- *
 * Reads
 * ------------------------------------------------------------------------- */

/** The review emails and chasers sent for this client, newest first. */
export function useDocMessages(businessId: string | null): DocsLoad<SentDocMessage[]> {
    return useDocsLoad<SentDocMessage[]>(businessId ? `doc-messages:${businessId}` : null, () => listDocMessages(businessId ?? ""));
}

/** The pre-share checklist for one doc. It reads again after every documents write (a fix, a share). */
export function useShareCheck(documentId: string | null): DocsLoad<ShareCheck> {
    return useDocsLoad<ShareCheck>(documentId ? `share-check:${documentId}` : null, () => getShareCheck(documentId ?? ""));
}

/** The last email of a kind sent about this doc; null when none. */
export function lastMessageOf(messages: readonly SentDocMessage[] | null, documentId: string, kind: SentDocMessageKind): SentDocMessage | null {
    return (messages ?? []).find((m) => m.kind === kind && m.documentIds.includes(documentId)) ?? null;
}

/* ------------------------------------------------------------------------- *
 * Facts the views show
 * ------------------------------------------------------------------------- */

/** "1" to "1.0": the house version always has its minor part. */
export function houseVersion(v: string | null): string {
    if (!v) return "1.0";
    return v.includes(".") ? v : `${v}.0`;
}

export interface OuraLines {
    /** Line 1 and line 2 as the doc has them; null when Oura has not read a page title. */
    title: string | null;
    version: string | null;
    /** What Oura keeps them as. */
    wantTitle: string;
    wantVersion: string;
    /** An open comment sits on the line, so Oura leaves it. */
    titleHeld: DocThread | null;
    versionHeld: DocThread | null;
}

/** The open comment whose quoted text is on this line, if any (B16: never rewrite under an open comment). */
function heldBy(line: string | null, threads: readonly DocThread[]): DocThread | null {
    const l = line?.trim();
    if (!l) return null;
    return threads.find((t) => !t.resolved && t.quoted?.trim() && (l.includes(t.quoted.trim()) || t.quoted.includes(l))) ?? null;
}

/** Oura's 2 lines: as they stand, as they should read, and whether an open comment holds either. Null for a doc with no code. */
export function ouraLines(doc: StaffDocument, detail: DocumentDetail | null, businessName: string, now: number, plan: BusinessDocuments["plan"] = null): OuraLines | null {
    const decided = decidedName(doc, businessName, now, plan);
    if (!decided) return null;
    const [title = null, version = null] = doc.pageTitle ? doc.pageTitle.split("\n").map((x) => x.trim()) : [];
    const threads = detail?.threads ?? [];
    return {
        title,
        version,
        wantTitle: decided.title,
        // empty on a doc with no version (#4874); an older doc keeps its version line
        wantVersion: lineTwoOf(businessName, doc.version ? houseVersion(doc.version) : null),
        titleHeld: heldBy(title, threads),
        versionHeld: heldBy(version, threads),
    };
}

/** Whether the name and both lines already read as the house format. */
export function namesCurrent(doc: StaffDocument, detail: DocumentDetail | null, businessName: string, now: number, plan: BusinessDocuments["plan"] = null): boolean | null {
    const decided = decidedName(doc, businessName, now, plan);
    const lines = ouraLines(doc, detail, businessName, now, plan);
    if (!decided || !lines) return null;
    return decided.nameMatches && lines.title === lines.wantTitle && (lines.version ?? "") === lines.wantVersion;
}

/** The reply-by has passed with questions still waiting on the client: the chaser's moment. */
export function chaserDue(doc: StaffDocument, now: number): boolean {
    const waiting = doc.questions?.waitingOnClient ?? 0;
    return doc.sharing.clientCanSee && waiting > 0 && Boolean(doc.replyBy) && (doc.replyBy ?? "") < myToday(now);
}

/** Review emails are for a shared doc with questions waiting on the client. */
export function reviewOpen(doc: StaffDocument): boolean {
    return doc.sharing.clientCanSee && (doc.questions?.waitingOnClient ?? 0) > 0;
}

/**
 * Why the doc cannot have its client email yet; null when it can (ourteam-backend#6700).
 * It is for a finished doc: staff marked it Done and the client can open it. The
 * server refuses the same cases; this only says why the button waits.
 */
export function clientEmailBlocked(doc: Pick<StaffDocument, "state" | "sharing">): string | null {
    if (doc.state !== "done") return "Mark it Done first";
    if (!doc.sharing.clientCanSee) return "Share it with the client first";
    return null;
}
